<script setup lang="ts">
import { computed, ref, watch } from "vue";
import useBranchStore from "@/stores/branch.store";
import { overlayTargetEl } from "@/utils/overlayTarget";

// App-wide "which clinic is this for?" prompt. Mounted once in App.vue and
// driven entirely by the branch store. Shown only to unrestricted users when
// more than one Branch exists and no clinic has been chosen for the session.
const { t } = useI18n();
const branchStore = useBranchStore();

const selected = ref<string | null>(null);

// No default selection — guessing the clinic is the exact bug this fixes. Reset
// each time the modal opens so a prior view never pre-fills.
watch(
  () => branchStore.modalOpen,
  (open) => {
    if (open)
      selected.value = null;
  },
);

const options = computed(() => branchStore.available);
const canConfirm = computed(() => Boolean(selected.value));

function onConfirm() {
  if (!selected.value)
    return;

  branchStore.confirmBranch(selected.value);
}

function onCancel() {
  branchStore.cancelBranch();
}

// Any dismissal (overlay/escape) is a cancel — never a silent submit.
function onModelValue(value: boolean) {
  if (!value)
    onCancel();
}
</script>

<template>
  <VDialog
    max-width="460"
    persistent
    :attach="overlayTargetEl"
    transition="dialog-transition"
    :model-value="branchStore.modalOpen"
    @update:model-value="onModelValue"
  >
    <VCard class="pa-2">
      <VCardText class="text-center pt-6 pb-2">
        <VAvatar
          size="56"
          color="primary"
          variant="tonal"
        >
          <VIcon
            icon="tabler-building-hospital"
            size="28"
          />
        </VAvatar>
      </VCardText>

      <VCardText class="text-center pt-0">
        <h5 class="text-h5 mb-2">
          {{ t("alkokh.branchSelection.title") }}
        </h5>
        <p class="text-body-2 text-medium-emphasis mb-0">
          {{ t("alkokh.branchSelection.subtitle") }}
        </p>
      </VCardText>

      <VCardText class="pt-2 pb-0">
        <VRadioGroup v-model="selected" hide-details>
          <VRadio
            v-for="option in options"
            :key="option.name"
            :label="option.branch"
            :value="option.name"
            class="mb-1"
          />
        </VRadioGroup>
      </VCardText>

      <VCardActions class="px-4 pb-4 pt-2">
        <VSpacer />
        <VBtn
          variant="tonal"
          color="secondary"
          @click="onCancel"
        >
          {{ t("alkokh.global.cancel") }}
        </VBtn>
        <VBtn
          variant="elevated"
          color="primary"
          :disabled="!canConfirm"
          @click="onConfirm"
        >
          {{ t("alkokh.branchSelection.confirm") }}
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
